/**
 * save.ts — What a cloud save contains, and how two of them combine.
 *
 * Pure by design: no Firebase, no storage, no clock beyond what it is handed.
 * Merging is the one part of cloud save that can quietly destroy something a
 * player earned, so it lives on its own where it can be tested exhaustively
 * rather than buried in the code that talks to the network.
 *
 * The document is deliberately tiny — the whole game's persisted state is a set
 * of small integers and a boolean — but it carries a `version` so a later
 * release can add per-level times or stats without having to guess what an old
 * client wrote.
 */

export const SAVE_VERSION = 1;

/** Everything that follows a player between devices. */
export interface SaveData {
  readonly version: typeof SAVE_VERSION;
  /** Completed campaign levels: unique, positive, sorted ascending. */
  readonly completed: readonly number[];
  readonly muted: boolean;
  /** Client epoch ms of the write. Only ever compared, never displayed. */
  readonly updatedAt: number;
}

/** A save with nothing in it. The starting point for a player with no account. */
export function emptySave(): SaveData {
  return { version: SAVE_VERSION, completed: [], muted: false, updatedAt: 0 };
}

/** Unique, positive, ascending — the one representation `completed` may take. */
function normaliseLevels(levels: Iterable<unknown>): number[] {
  const clean = new Set<number>();
  for (const n of levels) if (Number.isInteger(n) && (n as number) > 0) clean.add(n as number);
  return [...clean].sort((a, b) => a - b);
}

/**
 * Combine two saves without losing anything anyone earned.
 *
 * **Completed levels are unioned.** A tick is a thing that happened — someone
 * walked that dog home — so a level finished on either side stays finished. The
 * alternative, letting the newer save overwrite, means beating a level on your
 * phone while your laptop is offline silently erases it, which is precisely the
 * bug that makes people distrust cloud saves.
 *
 * **Mute is taken from whichever save was written more recently**, because a
 * setting has no "more" or "less" to union — the last thing the player asked for
 * is the right answer. Ties go to `a`, so callers pass local first and a clock
 * that hasn't ticked can't flip the setting back.
 */
export function mergeSaves(a: SaveData, b: SaveData): SaveData {
  const newer = b.updatedAt > a.updatedAt ? b : a;
  return {
    version: SAVE_VERSION,
    completed: normaliseLevels([...a.completed, ...b.completed]),
    muted: newer.muted,
    updatedAt: Math.max(a.updatedAt, b.updatedAt),
  };
}

/**
 * Turn an untrusted document into a `SaveData`, or `null` if it isn't one.
 *
 * Same posture as `progress.ts`: anything unrecognised is discarded rather than
 * trusted. The input here has been over a network and through a database that a
 * determined player can write to directly, so "it parsed as JSON" is not
 * evidence of anything. Junk *inside* a well-formed document is dropped field by
 * field — a save with one bad level number is still worth the nine good ones.
 */
export function parseSave(raw: unknown): SaveData | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const doc = raw as Record<string, unknown>;

  // An unknown version is a document written by a future release. Refusing it
  // is the honest move: merging fields we don't understand would write back a
  // lossy copy and destroy whatever that version was keeping.
  if (doc.version !== SAVE_VERSION) return null;
  if (!Array.isArray(doc.completed)) return null;

  return {
    version: SAVE_VERSION,
    completed: normaliseLevels(doc.completed),
    muted: doc.muted === true,
    updatedAt: typeof doc.updatedAt === 'number' && Number.isFinite(doc.updatedAt) ? doc.updatedAt : 0,
  };
}
